import React, { useState } from 'react';
import Link from 'next/link';
import { QUESTIONS } from '../constants';
import AssessmentFooter from '../AssessmentFooter';
import { ArrowRight } from '../icons';
import { NewLogo } from '@/components/ui/icons/Logo';
import { useAssessmentContent } from '../content/context';

interface QuestionScreenProps {
  currentQ: number;
  answers: (number | null)[];
  onAnswer: (idx: number) => void;
  onNext: () => void;
  onBack: () => void;
}

const QuestionScreen: React.FC<QuestionScreenProps> = ({ currentQ, answers, onAnswer, onNext, onBack }) => {
  const { pillars, questions } = useAssessmentContent();
  const [shake, setShake] = useState(false);
  const total = QUESTIONS.length + 1;
  // Scoring logic (pillar index + weights) stays in code; copy comes from CMS.
  const logic = QUESTIONS[currentQ];
  const copy = questions[currentQ];
  const pct = Math.round((currentQ / total) * 100);

  const handleNext = () => {
    if (answers[currentQ] === null) {
      setShake(true);
      setTimeout(() => setShake(false), 800);
      return;
    }
    onNext();
  };

  return (
    <div className="animate-fade-in">
      <nav
        className="bg-white border-b border-a-border px-10 flex items-center justify-between sticky top-0 z-50"
        style={{ height: '58px' }}
      >
        <Link href="/">
          <NewLogo height="60" title="Foxbox Digital" />
        </Link>
        <div className="font-semibold text-a-navy" style={{ fontSize: '11px' }}>
          Question {currentQ + 1} of {total}
        </div>
      </nav>

      <div
        className="bg-a-off-white flex flex-col items-center p-12"
        style={{ minHeight: 'calc(100vh - 58px - 42px)' }}
      >
        <div
          className="bg-white border border-a-border w-full"
          style={{
            padding: '40px 44px',
            maxWidth: '640px',
            borderRadius: '10px',
          }}
        >
          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-a-text-secondary" style={{ fontSize: '11px' }}>
                Question {currentQ + 1} of {total}
              </span>
              <span className="font-bold text-a-navy" style={{ fontSize: '11px' }}>
                {pct}% complete
              </span>
            </div>
            <div className="bg-a-light-gray overflow-hidden" style={{ height: '5px', borderRadius: '3px' }}>
              <div
                className="bg-a-orange h-full transition-all duration-300"
                style={{ width: `${pct}%`, borderRadius: '3px' }}
              />
            </div>
          </div>

          {/* Category */}
          <div
            className="inline-flex items-center bg-a-light-gray text-a-navy font-bold uppercase mb-4"
            style={{
              fontSize: '9px',
              letterSpacing: '0.07em',
              padding: '4px 10px',
              borderRadius: '4px',
              marginBottom: '16px',
              gap: '6px',
            }}
          >
            <div
              className="cat-dot"
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: '#f88143',
                flexShrink: 0,
              }}
            />
            <span>{pillars[logic.pillar]}</span>
          </div>

          {/* Question */}
          <div
            className="font-bold text-a-navy mb-7"
            style={{
              fontSize: '19px',
              lineHeight: '1.35',
              marginBottom: '28px',
            }}
          >
            {copy.question}
          </div>

          {/* Options */}
          <div className="flex flex-col mb-8" style={{ gap: '10px' }} role="radiogroup" aria-label={copy.question}>
            {copy.options.map((opt, i) => {
              const selected = answers[currentQ] === i;
              return (
                <div
                  key={i}
                  className={`flex items-center cursor-pointer font-medium transition-all ${
                    selected ? 'border-a-orange' : 'border-a-border hover:border-a-navy-light'
                  } border`}
                  style={{
                    padding: '15px 18px',
                    fontSize: '13px',
                    color: '#1a2b3c',
                    lineHeight: '1.4',
                    borderRadius: '8px',
                    gap: '14px',
                    background: selected ? '#fff7f2' : 'white',
                    ...(shake && answers[currentQ] === null ? { borderColor: '#f88143' } : {}),
                  }}
                  onClick={() => onAnswer(i)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onAnswer(i);
                    }
                  }}
                  role="radio"
                  aria-checked={selected}
                  tabIndex={0}
                >
                  <div
                    className="flex items-center justify-center flex-shrink-0 transition-all"
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      border: `2px solid ${selected ? '#f88143' : '#d6dee6'}`,
                      background: selected ? '#f88143' : 'transparent',
                    }}
                  >
                    {selected && (
                      <div
                        style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: '#ffffff',
                        }}
                      />
                    )}
                  </div>
                  {opt}
                </div>
              );
            })}
          </div>

          {/* Nav */}
          <div className="flex justify-between items-center pt-6 border-t border-a-light-gray">
            <button
              className="text-a-text-muted font-medium bg-transparent border border-a-border rounded font-sans hover:border-a-navy hover:text-a-navy transition-all cursor-pointer"
              style={{
                fontSize: '12px',
                padding: '9px 18px',
                outline: 'none',
                boxShadow: 'none',
                visibility: currentQ === 0 ? 'hidden' : 'visible',
              }}
              onClick={onBack}
            >
              ← Back
            </button>
            <button
              className="bg-a-orange text-white font-bold font-sans rounded flex items-center cursor-pointer hover:bg-a-orange-dark transition-colors border-0"
              style={{
                fontSize: '12px',
                padding: '9px 26px',
                borderRadius: '6px',
                border: 'none',
                letterSpacing: '0.04em',
                gap: '6px',
                outline: 'none',
                boxShadow: 'none',
              }}
              onClick={handleNext}
            >
              Next <ArrowRight width={14} height={14} />
            </button>
          </div>
        </div>
      </div>

      <AssessmentFooter />
    </div>
  );
};

export default QuestionScreen;
